import axios from "axios";
import { getBackendUrl } from "@src/utils/getBackendUrl";
import { getAuthHeaders } from "@src/utils/getAuthHeader";
import { CityAlias, CityAliasesResponse } from "./types";

type SortBy = "sfCity" | "mpCity" | "state" | "createdBy" | "createdOn";
type SortDirection = "asc" | "desc";

interface ListCityAliasesParams {
	page: number;
	pageSize: number;
	searchTerm?: string;
	sortBy?: SortBy;
	sortDirection?: SortDirection;
}

const firstString = (source: Record<string, unknown>, keys: Array<string>): string => {
	for (const key of keys) {
		const value = source[key];
		if (typeof value === "string" && value.trim() !== "") {
			return value;
		}
	}

	return "";
};

const toNumberOrNull = (value: unknown): number | null => {
	if (typeof value === "number" && Number.isFinite(value)) {
		return value;
	}

	if (typeof value === "string" && value.trim() !== "") {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : null;
	}

	return null;
};

const toCityAlias = (raw: Record<string, unknown>, index: number): CityAlias => {
	const props = typeof raw.props === "object" && raw.props !== null ? (raw.props as Record<string, unknown>) : raw;
	const idCandidate = props.cityAliasId ?? props.id ?? props.aliasId;

	return {
		id: typeof idCandidate === "number" || typeof idCandidate === "string" ? idCandidate : `city-alias-${index}`,
		marketplaceId: toNumberOrNull(props.marketplaceId),
		marketplaceCity: firstString(props, ["marketplaceCity", "marketplaceCityName", "mpCity"]),
		stageFrontCity: firstString(props, ["stageFrontCity", "stageFrontCityName", "sfCity"]),
		state: firstString(props, ["stageFrontStateCode", "stageFrontState", "sfStateCode", "stateCode", "state", "marketplaceStateCode"]),
		createdBy: firstString(props, ["createdBy", "createdByUser", "user"]),
		createdOn: firstString(props, ["createdOn", "createdAt", "createdDate"]),
	};
};

export const cityAliasesService = {
	async listCityAliases(params: ListCityAliasesParams): Promise<CityAliasesResponse> {
		const response = await axios.get(`${getBackendUrl()}/city-aliases`, {
			params: {
				page: params.page,
				pageSize: params.pageSize,
				...(params.searchTerm ? { searchTerm: params.searchTerm } : {}),
				...(params.sortBy ? { sortBy: params.sortBy } : {}),
				...(params.sortDirection ? { sortDirection: params.sortDirection } : {}),
			},
			headers: getAuthHeaders(),
		});

		const payload = response.data ?? {};
		const itemsRaw = Array.isArray(payload) ? payload : Array.isArray(payload.items) ? payload.items : [];
		const items = itemsRaw
			.filter((item: unknown): item is Record<string, unknown> => typeof item === "object" && item !== null)
			.map(toCityAlias);

		return {
			items,
			total: typeof payload.total === "number" ? payload.total : items.length,
		};
	},

	async deleteCityAlias(cityAliasId: number | string): Promise<void> {
		const normalizedId = typeof cityAliasId === "number" ? cityAliasId : Number(cityAliasId);

		if (!Number.isInteger(normalizedId)) {
			throw new Error("No valid city alias ID to delete.");
		}

		await axios.delete(`${getBackendUrl()}/city-aliases`, {
			data: { cityAliasIds: [normalizedId] },
			headers: getAuthHeaders(),
		});
	},
};
